import { nameFields, nameEntryMode } from "../utils/individualName";
// ClientsPage.jsx

import "./css/ClientsPage.css";

import { createElement, useMemo, useEffect, useState, useRef } from "react";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { useNavigate, useLocation, Link } from "react-router-dom";

import { Toast } from "primereact/toast";
import { motion } from "framer-motion";
import Select from "react-select";

import iconHomeA from "../images/home-G.svg";
import clientsTopLogo from "../images/mykyc-logo-blue-800px.png";
import ClientsTable from "../components/clients/ClientsTable";
import ClientDropIntakeRouter from "../components/clients/ClientDropIntakeRouter";
import ClientsDocumentDropZone from "../components/clients/ClientsDocumentDropZone";
import SearchBar from "../components/ui/SearchBar";
import FilterButton from "../components/ui/FilterButton";
import AddClientModal from "../components/clients/AddClientModal";
import EditClientModal from "../components/clients/EditClientModal";
import ViewClientModal from "../components/clients/ViewClientModal";
import { useStaffTopBand } from "../components/ui/StaffTopBandContext";
import { MAIL_API_BASE } from "../config/api";

import { appConfirm } from "../components/ui/appConfirm";
import { confirmedKycFields, confirmedCvFields, intakeRequest } from "../utils/clientDocumentIntake";
import { clientToKycCustomer } from "../utils/syncClientKyc";
import { formatManualClientName } from "../utils/clientName";

import { useAuth } from "../auth/AuthProvider";
import { roleLabel, canDeleteClient } from "../auth/permissions";
import {
  RELATIONSHIP_RISK_MODEL_2_LEVEL,
  RELATIONSHIP_RISK_MODEL_3_LEVEL,
  normalizeRelationshipRiskModel,
} from "../utils/relationshipRiskRules";

import {
  collection,
  arrayUnion,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  orderBy,
  query,
  doc,
  documentId,
  where,
  getCountFromServer,
  writeBatch,
} from "firebase/firestore";

const pageAnim = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

function formatDate(ts) {
  if (!ts) return "—";
  const d = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(d);
}

const KYC_FLOW_LABEL = {
  not_sent: "to send",
  sent: "sent",
  to_review: "to review",
  approved: "approved",
};

const DEBUG_CLIENTS_PERMISSIONS = true;

const API_BASE = MAIL_API_BASE;

const ORG_GEO_PREFERRED_COUNTRY = {
  uae: "AE",
  switzerland: "CH",
};

const RISK_FILTER_OPTIONS = [
  { value: "", label: "All risk types" },
  { value: "standard", label: "Standard" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "tbd", label: "To be determined" },
];

const KYC_STATUS_FILTER_OPTIONS = [
  { value: "", label: "All KYC statuses" },
  { value: "incomplete", label: "Incomplete" },
  { value: "complete", label: "Complete" },
];

function orgGeoToPreferredCountry(value) {
  return ORG_GEO_PREFERRED_COUNTRY[String(value || "").trim().toLowerCase()] || "";
}

async function apiFetch(path, { method = "GET", token, body } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json?.ok === false) {
    throw new Error(json?.error || json?.message || "Request failed");
  }

  return json;
}

function debugClientsPermissions(label, details = {}) {
  if (!DEBUG_CLIENTS_PERMISSIONS) return;
  console.log(`[Clients permissions debug] ${label}`, details);
}

function staffAccessQuery(baseRef, { orgId, role, teamId, uid }, ...extraFilters) {
  if (role === "manager") {
    return query(baseRef, where("orgId", "==", orgId), ...extraFilters);
  }

  if (teamId) {
    return query(
      baseRef,
      where("orgId", "==", orgId),
      where("accessScope", "==", "team"),
      where("teamId", "==", teamId),
      ...extraFilters
    );
  }

  return query(
    baseRef,
    where("orgId", "==", orgId),
    where("accessScope", "==", "individual"),
    where("ownerUid", "==", uid),
    ...extraFilters
  );
}

function kycFlowSearchText(stage) {
  const s = String(stage || "").toLowerCase();
  if (s === "to_review") return "to review pending awaiting";
  if (s === "not_sent") return "to send todo";
  if (s === "sent") return "sent link";
  if (s === "approved") return "approved";
  return (KYC_FLOW_LABEL[s] || s).replaceAll("_", " ");
}

function normalizeRiskFilterLevel(value) {
  const v = String(value || "").trim().toLowerCase();
  if (v === "high") return "high";
  if (v === "medium") return "medium";
  if (v === "low" || v === "standard") return "standard";
  if (v === "to be determined" || v === "tbd" || v === "—" || v === "-") return "tbd";
  return "";
}

function riskFilterModelFromKycData(data) {
  return normalizeRelationshipRiskModel(data?.finalOverview?.relationshipRiskResult?.model);
}

function riskFilterLevelFromKycData(data) {
  return normalizeRiskFilterLevel(
    data?.finalOverview?.calculatedRiskRating ||
      data?.internalUse?.calculatedRiskRating ||
      data?.internalUse?.overallRiskLevel ||
      data?.internalUse?.riskRating ||
      ""
  );
}

function chunkArray(values, size) {
  const chunks = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
}

async function attachRiskFilterLevels(rows) {
  const ids = rows.map((row) => row.id).filter(Boolean);
  if (!ids.length) return rows;

  const riskByClientId = {};
  const riskModelByClientId = {};
  const chunks = chunkArray(ids, 10);

  await Promise.all(
    chunks.map(async (chunk) => {
      const snap = await getDocs(
        query(collection(db, "kyc"), where(documentId(), "in", chunk))
      );
      snap.docs.forEach((kycDoc) => {
        const data = kycDoc.data();
        riskByClientId[kycDoc.id] = riskFilterLevelFromKycData(data);
        riskModelByClientId[kycDoc.id] = riskFilterModelFromKycData(data);
      });
    })
  );

  return rows.map((row) => ({
    ...row,
    riskFilterLevel: riskByClientId[row.id] || "tbd",
    riskFilterModel: riskModelByClientId[row.id] || RELATIONSHIP_RISK_MODEL_3_LEVEL,
  }));
}

export default function ClientsPage() {
  const nav = useNavigate();
  const location = useLocation();

  const toastRef = useRef(null);
  const deleteTargetRef = useRef(null);

  // ✅ single source of truth (no auth/org checks here)
  const { isAdmin, me, org, role, authLoading } = useAuth();

  const myOrgId = String(me?.orgId || "").trim();
  const myTeamId = String(me?.teamId || "").trim();
  const myTeamName = String(me?.teamName || "").trim();
  const myUid = String(auth.currentUser?.uid || me?.uid || "").trim();
  const orgName = String(org?.name || "").trim();
  const orgPreferredPhoneCountry = orgGeoToPreferredCountry(org?.geo);

  const isReady = !authLoading && (isAdmin || !!myOrgId);

  // clients
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // search
  const [search, setSearch] = useState("");
  const [adminOrgFilterId, setAdminOrgFilterId] = useState("");
  const [adminOrgOptions, setAdminOrgOptions] = useState([]);
  const [managerAccessFilterValue, setManagerAccessFilterValue] = useState("");
  const [riskFilterValue, setRiskFilterValue] = useState("");
  const [kycStatusFilterValue, setKycStatusFilterValue] = useState("");
  const [riskFilterOpen, setRiskFilterOpen] = useState(false);
  const riskFilterRef = useRef(null);
  const [managerAccessOptions, setManagerAccessOptions] = useState({
    teams: [],
    staffMembers: [],
  });

  const adminOrgSelectOptions = useMemo(
    () => [
      { value: "", label: "All organisations" },
      ...adminOrgOptions.map((o) => ({
        value: o.orgId,
        label: o.name,
      })),
    ],
    [adminOrgOptions]
  );

  const adminOrgSelectedOption = useMemo(
    () =>
      adminOrgSelectOptions.find((o) => o.value === adminOrgFilterId) ||
      adminOrgSelectOptions[0],
    [adminOrgFilterId, adminOrgSelectOptions]
  );

  const managerAccessSelectOptions = useMemo(() => {
    const teamOptions = (managerAccessOptions.teams || []).map((team) => ({
      value: `team:${team.teamId}`,
      label: team.teamName || "Shared team",
      type: "team",
      teamId: String(team.teamId || "").trim(),
      teamName: team.teamName || "Shared team",
      staffCount: team.staffCount || 0,
    }));
    const allStaffOptions = (managerAccessOptions.staffMembers || [])
      .map((staff) => ({
        value: `staff:${staff.uid}`,
        label: staff.fullName || staff.email || staff.uid,
        type: "staff",
        uid: String(staff.uid || "").trim(),
        email: staff.email || "",
        role: staff.role || "staff",
        teamId: String(staff.teamId || "").trim(),
        teamName: String(staff.teamName || "").trim(),
        isCurrentUser: String(staff.uid || "").trim() === myUid,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const managerOwnOption = allStaffOptions.filter((opt) => opt.isCurrentUser);
    const staffOptions = allStaffOptions.filter((opt) => !opt.isCurrentUser);

    return [
      { value: "", label: "All teams and staff", type: "all" },
      ...managerOwnOption,
      ...teamOptions,
      ...staffOptions,
    ];
  }, [managerAccessOptions, myUid]);

  const managerAccessSelectedOption = useMemo(
    () =>
      managerAccessSelectOptions.find((o) => o.value === managerAccessFilterValue) ||
      managerAccessSelectOptions[0],
    [managerAccessFilterValue, managerAccessSelectOptions]
  );

  const managerCreationTarget = useMemo(() => {
    if (role !== "manager") return null;
    const selected = managerAccessSelectedOption;
    if (!selected || selected.type === "all") return null;

    if (selected.type === "team") {
      return {
        accessScope: "team",
        ownerUid: "",
        teamId: selected.teamId || String(selected.value || "").replace(/^team:/, ""),
        teamName: selected.teamName || selected.label || "",
      };
    }

    if (selected.type === "staff") {
      if (selected.isCurrentUser) {
        return {
          accessScope: "individual",
          ownerUid: selected.uid || myUid,
          teamId: "",
          teamName: "",
        };
      }

      if (selected.teamId) {
        return {
          accessScope: "team",
          ownerUid: "",
          teamId: selected.teamId,
          teamName: selected.teamName || "",
        };
      }

      return {
        accessScope: "individual",
        ownerUid: selected.uid || String(selected.value || "").replace(/^staff:/, ""),
        teamId: "",
        teamName: "",
      };
    }

    return null;
  }, [role, managerAccessSelectedOption, myUid]);

  const clientsSelectStyles = useMemo(
    () => ({
      control: (base) => ({
        ...base,
        height: "38px",
        minHeight: "38px",
        borderRadius: "0.375rem",
        borderColor: "#DFE1E5",
        boxShadow: "none",
        fontSize: "14px",
      }),
      valueContainer: (base) => ({
        ...base,
        height: "38px",
        padding: "0 12px",
      }),
      placeholder: (base) => ({
        ...base,
        color: "#a5acb2",
        fontWeight: 300,
        fontSize: "14px",
      }),
      singleValue: (base) => ({
        ...base,
        color: "#4c665e",
        letterSpacing: "-0.01em",
        fontSize: "14px",
      }),
      input: (base) => ({
        ...base,
        color: "#4c665e",
        margin: 0,
        padding: 0,
        fontSize: "14px",
      }),
      indicatorsContainer: (base) => ({
        ...base,
        height: "38px",
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? "rgba(155,175,165,0.25)"
          : state.isFocused
          ? "rgba(155,175,165,0.15)"
          : "transparent",
        color: "#2f3a4a",
        cursor: "pointer",
        fontSize: "14px",
      }),
      indicatorSeparator: () => ({ display: "none" }),
      menu: (base) => ({
        ...base,
        zIndex: 5000,
      }),
      menuPortal: (base) => ({
        ...base,
        zIndex: 5000,
      }),
    }),
    []
  );

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    const orgFilterId = String(adminOrgFilterId || "").trim();
    const managerFilter = String(managerAccessFilterValue || "").trim();
    const riskFilter = String(riskFilterValue || "").trim();
    const kycStatusFilter = String(kycStatusFilterValue || "").trim();

    return clients.filter((c) => {
      if (isAdmin && orgFilterId && String(c.orgId || "").trim() !== orgFilterId) {
        return false;
      }
      if (!isAdmin && role === "manager" && managerFilter) {
        const [filterType, filterId] = managerFilter.split(":");
        if (filterType === "team") {
          if (
            String(c.accessScope || "").trim() !== "team" ||
            String(c.teamId || "").trim() !== filterId
          ) {
            return false;
          }
        } else if (filterType === "staff") {
          if (
            String(c.accessScope || "").trim() !== "individual" ||
            String(c.ownerUid || "").trim() !== filterId
          ) {
            return false;
          }
        }
      }

      if (
        riskFilter &&
        normalizeRiskFilterLevel(c.riskFilterLevel || c.finalOverview?.calculatedRiskRating) !== riskFilter
      ) {
        return false;
      }

      if (kycStatusFilter) {
        const currentKycStatus = c.kycCompleted ? "complete" : "incomplete";
        if (currentKycStatus !== kycStatusFilter) return false;
      }

      if (!q) return true;

      const name = (c.clientName || "").toLowerCase();
      const type = (c.clientType || "").toLowerCase();
      const status = (c.status || "").toLowerCase();
      const kycFlow = kycFlowSearchText(c.kycFlowStage).toLowerCase();
      const kycStatus = c.kycCompleted ? "complete" : "incomplete uncomplete";

      return (
        kycFlow.includes(q) ||
        kycStatus.includes(q) ||
        name.includes(q) ||
        type.includes(q) ||
        status.includes(q)
      );
    });
  }, [
    clients,
    search,
    isAdmin,
    role,
    adminOrgFilterId,
    managerAccessFilterValue,
    riskFilterValue,
    kycStatusFilterValue,
  ]);

  const riskFilterOptions = useMemo(() => {
    const orgFilterId = isAdmin ? String(adminOrgFilterId || "").trim() : "";
    const managerFilter = String(managerAccessFilterValue || "");
    const scoped = clients.filter((c) => {
      if (isAdmin && orgFilterId && String(c.orgId || "").trim() !== orgFilterId) {
        return false;
      }
      if (!isAdmin && role === "manager" && managerFilter) {
        const [filterType, filterId] = managerFilter.split(":");
        if (filterType === "team") {
          return (
            String(c.accessScope || "").trim() === "team" &&
            String(c.teamId || "").trim() === filterId
          );
        }
        if (filterType === "staff") {
          return (
            String(c.accessScope || "").trim() === "individual" &&
            String(c.ownerUid || "").trim() === filterId
          );
        }
      }
      return true;
    });
    const knownModels = scoped
      .map((client) => normalizeRelationshipRiskModel(client.riskFilterModel))
      .filter(Boolean);
    const hideMedium =
      knownModels.length > 0 &&
      knownModels.every((model) => model === RELATIONSHIP_RISK_MODEL_2_LEVEL);

    return RISK_FILTER_OPTIONS.filter((option) => option.value !== "medium" || !hideMedium);
  }, [adminOrgFilterId, clients, isAdmin, managerAccessFilterValue, role]);

  useEffect(() => {
    if (
      riskFilterValue === "medium" &&
      !riskFilterOptions.some((option) => option.value === "medium")
    ) {
      setRiskFilterValue("");
    }
  }, [riskFilterOptions, riskFilterValue]);

  const hasActiveClientFilter = !!riskFilterValue || !!kycStatusFilterValue;

  // inbox count
  const [kycInboxCount, setKycInboxCount] = useState(0);

  // modals
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addClientDroppedFiles, setAddClientDroppedFiles] = useState(null);
  const [matchingFiles, setMatchingFiles] = useState(null);
  const [addClientInitialIntake, setAddClientInitialIntake] = useState(null);
  const [editClientInitialIntake, setEditClientInitialIntake] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editClientDroppedFiles, setEditClientDroppedFiles] = useState(null);

  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewingClient, setViewingClient] = useState(null);

  const editingClientOrgPreferredPhoneCountry = useMemo(() => {
    if (!isAdmin) return orgPreferredPhoneCountry;
    const editingOrgId = String(editingClient?.orgId || "").trim();
    if (!editingOrgId) return "";
    const editingOrg = adminOrgOptions.find((o) => o.orgId === editingOrgId);
    return orgGeoToPreferredCountry(editingOrg?.geo);
  }, [isAdmin, orgPreferredPhoneCountry, editingClient?.orgId, adminOrgOptions]);

  // ---------------------------
  // ✅ Smooth Loading Overlay (no flicker)
  // ---------------------------
  const [showLoadingUI, setShowLoadingUI] = useState(false);
  const loadingBeganAtRef = useRef(0);
  const showTimerRef = useRef(null);
  const hideTimerRef = useRef(null);

  useEffect(() => {
    clearTimeout(showTimerRef.current);
    clearTimeout(hideTimerRef.current);

    const SHOW_DELAY_MS = 180;
    const MIN_VISIBLE_MS = 280;

    if (loading) {
      loadingBeganAtRef.current = Date.now();
      showTimerRef.current = setTimeout(
        () => setShowLoadingUI(true),
        SHOW_DELAY_MS
      );
    } else {
      const elapsed = Date.now() - (loadingBeganAtRef.current || Date.now());
      const remaining = showLoadingUI
        ? Math.max(0, MIN_VISIBLE_MS - elapsed)
        : 0;

      hideTimerRef.current = setTimeout(
        () => setShowLoadingUI(false),
        remaining
      );
    }

    return () => {
      clearTimeout(showTimerRef.current);
      clearTimeout(hideTimerRef.current);
    };
  }, [loading, showLoadingUI]);

  // ---------------------------
  // ✅ Load clients
  // ---------------------------
  async function loadClients() {
    try {
      setErr("");
      setLoading(true);

      const baseRef = collection(db, "clients");

      let q;

      // ✅ Admin sees all
      if (isAdmin) {
        debugClientsPermissions("loadClients query", {
          collection: "clients",
          scope: "admin",
          filters: [],
          orderBy: "clientName asc",
        });
        q = query(baseRef, orderBy("clientName", "asc"));
      } else {
        // Prevent staff queries when orgId is empty
        // This avoids running where("orgId","==","") (bad)
        if (!myOrgId) {
          debugClientsPermissions("loadClients skipped", {
            reason: "missing myOrgId",
            isAdmin,
            role,
            me,
          });
          setClients([]);
          return;
        }
      // ✅ Manager sees all org clients; staff sees team clients or own clients if no team.
        debugClientsPermissions("loadClients query", {
          collection: "clients",
          scope: role === "manager" ? "org" : myTeamId ? "team" : "individual",
          filters: {
            orgId: myOrgId,
            ...(role === "manager"
              ? {}
              : myTeamId
              ? { accessScope: "team", teamId: myTeamId }
              : { accessScope: "individual", ownerUid: myUid }),
          },
          orderBy: "clientName asc",
        });
        if (role === "manager") {
          q = query(
            baseRef,
            where("orgId", "==", myOrgId),
            orderBy("clientName", "asc")
          );
        } else {
          if (!myTeamId && !myUid) {
            debugClientsPermissions("loadClients skipped", {
              reason: "missing myTeamId and myUid",
              role,
              myOrgId,
              me,
            });
            setClients([]);
            return;
          }
          q = staffAccessQuery(
            baseRef,
            { orgId: myOrgId, role, teamId: myTeamId, uid: myUid },
            orderBy("clientName", "asc")
          );
        }
      }

      const snap = await getDocs(q);
      const rows = await attachRiskFilterLevels(
        snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      );
      debugClientsPermissions("loadClients success", {
        count: rows.length,
        firstClient: rows[0]
          ? {
              id: rows[0].id,
              orgId: rows[0].orgId,
              clientName: rows[0].clientName,
            }
          : null,
      });

      setClients(rows);
    } catch (e) {
      debugClientsPermissions("loadClients failed", {
        code: e?.code,
        message: e?.message,
        isAdmin,
        role,
        myOrgId,
        myTeamId,
        uid: auth.currentUser?.uid || null,
        me,
        org,
      });
      setErr(e?.message || "Failed to load clients");
    } finally {
      setLoading(false);
    }
  }

  async function loadAdminOrgOptions() {
    if (!isAdmin) {
      setAdminOrgOptions([]);
      setAdminOrgFilterId("");
      return;
    }

    try {
      const snap = await getDocs(query(collection(db, "orgs"), orderBy("name", "asc")));
      const rows = snap.docs
        .map((d) => ({
          orgId: d.id,
          name: String(d.data()?.name || d.id).trim(),
          geo: String(d.data()?.geo || "").trim(),
        }))
        .filter((o) => o.orgId)
        .sort((a, b) => a.name.localeCompare(b.name));

      setAdminOrgOptions(rows);
    } catch (e) {
      debugClientsPermissions("loadAdminOrgOptions failed", {
        code: e?.code,
        message: e?.message,
      });
      setAdminOrgOptions([]);
    }
  }

  async function loadManagerAccessOptions() {
    if (isAdmin || role !== "manager") {
      setManagerAccessOptions({ teams: [], staffMembers: [] });
      setManagerAccessFilterValue("");
      return;
    }

    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error("You must be signed in.");

      const json = await apiFetch("/myKyc/staff/client-access-options", { token });
      setManagerAccessOptions({
        teams: Array.isArray(json.teams) ? json.teams : [],
        staffMembers: Array.isArray(json.staffMembers) ? json.staffMembers : [],
      });
    } catch (e) {
      debugClientsPermissions("loadManagerAccessOptions failed", {
        code: e?.code,
        message: e?.message,
      });
      setManagerAccessOptions({ teams: [], staffMembers: [] });
    }
  }

  // ---------------------------
  // ✅ Load inbox count (staff only)
  // ---------------------------
  async function loadKycInboxCount() {
    try {
      // ---- KYC forms pending ----
      let qKyc;
      if (isAdmin) {
        qKyc = query(
          collection(db, "kyc_submissions"),
          where("status", "==", "pending")
        );
      } else {
      if (!myOrgId || (role !== "manager" && !myTeamId && !myUid)) {
          debugClientsPermissions("loadKycInboxCount skipped", {
            reason: "missing myOrgId or myTeamId",
            isAdmin,
            role,
            me,
          });
          return setKycInboxCount(0);
        }
        qKyc =
          staffAccessQuery(
            collection(db, "kyc_submissions"),
            { orgId: myOrgId, role, teamId: myTeamId, uid: myUid },
            where("status", "==", "pending")
          );
      }

      debugClientsPermissions("loadKycInboxCount kyc_submissions query", {
        collection: "kyc_submissions",
        scope: isAdmin ? "admin" : "org",
        filters: isAdmin
          ? { status: "pending" }
          : { orgId: myOrgId, status: "pending" },
      });

      // ---- Documents pending review ----
      let qDocs;
      if (isAdmin) {
        qDocs = query(
          collection(db, "documentSubmissions"),
          where("type", "==", "documents"),
          where("status", "==", "pending_review")
        );
      } else {
        qDocs =
          staffAccessQuery(
            collection(db, "documentSubmissions"),
            { orgId: myOrgId, role, teamId: myTeamId, uid: myUid },
            where("type", "==", "documents"),
            where("status", "==", "pending_review")
          );
      }

      debugClientsPermissions("loadKycInboxCount documentSubmissions query", {
        collection: "documentSubmissions",
        scope: isAdmin ? "admin" : "org",
        filters: isAdmin
          ? { type: "documents", status: "pending_review" }
          : { orgId: myOrgId, type: "documents", status: "pending_review" },
      });

      const [kycResult, docsResult] = await Promise.allSettled([
        getCountFromServer(qKyc),
        getCountFromServer(qDocs),
      ]);

      debugClientsPermissions("loadKycInboxCount results", {
        kycSubmissions: {
          status: kycResult.status,
          count:
            kycResult.status === "fulfilled"
              ? kycResult.value.data().count || 0
              : null,
          error:
            kycResult.status === "rejected"
              ? {
                  code: kycResult.reason?.code,
                  message: kycResult.reason?.message,
                }
              : null,
        },
        documentSubmissions: {
          status: docsResult.status,
          count:
            docsResult.status === "fulfilled"
              ? docsResult.value.data().count || 0
              : null,
          error:
            docsResult.status === "rejected"
              ? {
                  code: docsResult.reason?.code,
                  message: docsResult.reason?.message,
                }
              : null,
        },
        authContext: {
          uid: auth.currentUser?.uid || null,
          email: auth.currentUser?.email || null,
          isAdmin,
          role,
          myOrgId,
          me,
          org,
          orgDisabled: org?.disabled === true,
        },
      });

      if (kycResult.status === "rejected") throw kycResult.reason;
      if (docsResult.status === "rejected") throw docsResult.reason;

      const kycCount = kycResult.value.data().count || 0;
      const docsCount = docsResult.value.data().count || 0;

      setKycInboxCount(kycCount + docsCount);
    } catch (e) {
      console.warn("Failed to load KYC inbox count:", e?.code, e?.message, e);
      debugClientsPermissions("loadKycInboxCount failed", {
        code: e?.code,
        message: e?.message,
        isAdmin,
        role,
        myOrgId,
        uid: auth.currentUser?.uid || null,
        me,
        org,
        orgDisabled: org?.disabled === true,
      });
      setKycInboxCount(0);
    }
  }

  // async function loadKycInboxCountOLD_onlyKYCforms() {
  //   try {
  //     let q;

  //     if (isAdmin) {
  //       q = query(
  //         collection(db, "kyc_submissions"),
  //         where("status", "==", "pending")
  //       );
  //     } else {
  //       if (!myOrgId) return setKycInboxCount(0);

  //       q = query(
  //         collection(db, "kyc_submissions"),
  //         where("orgId", "==", myOrgId),
  //         where("status", "==", "pending")
  //       );
  //     }

  //     const snap = await getCountFromServer(q);
  //     setKycInboxCount(snap.data().count || 0);
  //   } catch (e) {
  //     console.warn("Failed to load KYC inbox count:", e?.code, e?.message, e);
  //     setKycInboxCount(0);
  //   }
  // }
  // async function loadKycInboxCount() {
  //   try {
  //     if (isAdmin) {
  //       setKycInboxCount(0);
  //       return;
  //     }

  //     // No query if org isn’t ready.
  //     if (!myOrgId) {
  //       setKycInboxCount(0);
  //       return;
  //     }

  //     const q = query(
  //       collection(db, "kyc_submissions"),
  //       where("orgId", "==", myOrgId),
  //       where("status", "==", "pending")
  //     );

  //     const snap = await getCountFromServer(q);
  //     setKycInboxCount(snap.data().count || 0);
  //   } catch (e) {
  //     console.warn("Failed to load KYC inbox count:", e);
  //     setKycInboxCount(0);
  //   }
  // }

  // ---------------------------
  // Delete client
  // ---------------------------
  async function handleDelete(clientId) {
    const token = auth.currentUser ? await auth.currentUser.getIdToken() : "";
    await apiFetch("/myKyc/admin/purge-client-data", {
      method: "POST",
      token,
      body: { clientId },
    });

    setClients((prev) => prev.filter((c) => c.id !== clientId));
  }

  function confirmDelete(client) {
    if (!canDeleteClient(role)) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Not available for your role",
        detail: "Deleting clients is reserved for Staff compliance and managers.",
        life: 3500,
      });
      return;
    }
    if (!client?.id) return;

    deleteTargetRef.current = client;
    const label = client?.clientName || client?.name || "this client";

    appConfirm({
      group: "appConfirm",
      header: "Confirm deletion",
      message: `Are you sure you want to delete ${label} ? This cannot be undone.`,
      defaultFocus: "reject",
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      scheme: "danger",
      icon: "bi bi-exclamation-triangle",
      accept: async () => {
        const c = deleteTargetRef.current;
        if (!c?.id) return;

        try {
          await handleDelete(c.id);
          toastRef.current?.show({
            severity: "success",
            summary: "Deleted",
            detail: `"${c.clientName || "Client"}" was deleted.`,
            life: 2500,
          });
        } catch (err) {
          toastRef.current?.show({
            severity: "error",
            summary: "Error",
            detail: err?.message || "Delete failed",
            life: 3500,
          });
        } finally {
          deleteTargetRef.current = null;
        }
      },
      reject: () => {
        deleteTargetRef.current = null;
      },
    });
  }

  // ---------------------------
  // Logout
  // ---------------------------
  async function handleLogout() {
    sessionStorage.setItem("mykyc:suppressClientsLogoutLoader", "1");
    sessionStorage.setItem("mykyc:loggedOut", "1");
    nav("/login", { replace: true });
    await signOut(auth);
  }

  // ---------------------------
  // Add client (staff only - simple + secure)
  // ---------------------------
  async function handleAddClient(payload) {
    try {
      const u = auth.currentUser;
      if (!u) throw new Error("You must be signed in.");

      if (isAdmin) {
        throw new Error("Admin accounts cannot create clients (staff only).");
      }

      const orgId = myOrgId;
      if (!orgId) throw new Error("Missing organisation (orgId).");
      const batch = writeBatch(db);

      const clientRef = payload.intakeSession ? doc(db, "clients", payload.intakeSession.id) : doc(collection(db, "clients"));
      const clientId = clientRef.id;
      const targetAccess =
        role === "manager"
          ? managerCreationTarget
          : myTeamId
          ? {
              accessScope: "team",
              ownerUid: "",
              teamId: myTeamId,
              teamName: myTeamName,
            }
          : {
              accessScope: "individual",
              ownerUid: u.uid,
              teamId: "",
              teamName: "",
            };

      if (!targetAccess) {
        throw new Error("Please select a team or staff member before adding a client.");
      }

      const { accessScope, ownerUid, teamId, teamName } = targetAccess;
      const clientName = String(payload.clientName || "").trim();

      const cvFields = confirmedCvFields(payload.intakeCv, payload.clientType);
      const clientDoc = {
        orgId,
        accessScope,
        ownerUid,
        teamId,
        teamName,
        createdByUid: u.uid,
        createdAt: serverTimestamp(),

        clientName,
        ...nameFields(payload),
        nameEntryMode: nameEntryMode(payload.nameEntryMode),
        clientType: payload.clientType,
        status: payload.status,

        address: payload.address || "",
        city: payload.city || "",
        country: payload.country || "",

        contactName: payload.contactName ?? null,
        contactEmail: payload.contactEmail ?? null,
        contactPhone: payload.contactPhone ?? null,

        joinDate: payload.joinDate || null,
        leaveDate: payload.leaveDate ?? null,

        kycCompleted: false,
        lastKycDate: null,
        kycFlowStage: "not_sent",
      };

      batch.set(clientRef, clientDoc);
      // The same atomic batch permanently locks the policy on the first record.
      const orgRef = doc(db, "orgs", orgId);
      const orgSnapshot = await getDoc(orgRef);
      const orgData = orgSnapshot.data() || {};
      if (nameEntryMode(orgData.nameEntryMode) !== nameEntryMode(payload.nameEntryMode)) {
        throw new Error("The organisation name setting changed. Reopen this form and try again.");
      }
      if (orgData.nameEntryLocked !== true) {
        batch.update(orgRef, { nameEntryLocked: true, nameEntryLockClientId: clientId });
      }

      batch.set(
        doc(db, "kyc", clientId),
        {
          clientId,
          orgId,
          accessScope,
          ownerUid,
          teamId,
          teamName,
          createdByUid: u.uid,
          customer: { ...clientToKycCustomer(clientDoc), ...confirmedKycFields({ ...payload.intakeKyc, country: payload.country }, payload.clientType),
            ...(cvFields.businessActivity !== undefined ? { businessActivity: cvFields.businessActivity } : {}) },
          ...(cvFields.sourceOfWealth ? { sourceOfFundsWealth: { sourceOfWealth: cvFields.sourceOfWealth } } : {}),
          updatedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
        },
        { merge: true }
      );

      const session = payload.intakeSession;
      if (!session?.clientSaved) {
        let existing = null;
        if (session?.creationAttempted) {
          try { existing = await getDoc(clientRef); }
          catch (error) {
            // Access rules may deny reads of a document that was never created.
            // A retry writes the same generated ID and still obeys create rules.
            if (error.code !== "permission-denied") throw error;
          }
        }
        if (existing?.exists()) {
          const data = existing.data();
          if (data.createdByUid !== u.uid || data.orgId !== orgId) throw new Error("This client could not be saved. Please close and try again.");
        } else {
          if (session) session.creationAttempted = true;
          await batch.commit();
        }
        if (session) session.clientSaved = true;
      }
      if (payload.intakeFiles?.length) await intakeRequest("attach", { clientId, files: payload.intakeFiles });

      await loadClients();
      setIsAddOpen(false);
      appConfirm({
        group: "appConfirm",
        className: "clientSavedKycDialog",
        header: "Client added successfully",
        message: createElement("span", null,
          createElement("strong", null, clientName || "Client"),
          " has been added.",
          createElement("br"),
          "Would you like to complete the KYC form now?"
        ),
        scheme: "neutral",
        icon: "bi bi-check2",
        acceptLabel: "Continue with KYC",
        rejectLabel: "Later",
        accept: () => nav(`/kyc/${clientId}`),
        reject: () => {},
      });
    } catch (err) {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: err?.message || "Add failed",
        life: 3500,
      });
      loadClients();
      throw err;
    }
  }

  // ---------------------------
  // Edit / View
  // ---------------------------
  function handleEdit(client, files = null, initialIntake = null) {
    setEditClientInitialIntake(initialIntake);
    setEditClientDroppedFiles(files);
    setEditingClient(client);
    setIsEditOpen(true);
  }

  function handleView(client) {
    setViewingClient(client);
    setIsViewOpen(true);
  }

  async function handleUpdateClient(payload) {
    if (!editingClient?.id) return;

    try {
      const clientName = formatManualClientName(payload.clientName);
      const session = payload.intakeSession;
      if (!payload.documentsOnly && !session?.clientSaved) {
        const batch = writeBatch(db);
        const cvFields = confirmedCvFields(payload.intakeCv, payload.clientType);
        batch.update(doc(db, "clients", editingClient.id), {
          clientName,
          ...nameFields(payload),
          clientType: payload.clientType,
          status: payload.status,

          address: payload.address || "",
          city: payload.city || "",
          country: payload.country || "",

          contactName: payload.contactName ?? null,
          contactEmail: payload.contactEmail ?? null,
          contactPhone: payload.contactPhone ?? null,

          joinDate: payload.joinDate ?? null,
          leaveDate: payload.leaveDate ?? null,
        });

        batch.set(
          doc(db, "kyc", editingClient.id),
          {
            customer: {
              ...clientToKycCustomer({
                clientName,
                ...nameFields(payload),
                clientType: payload.clientType,
                contactEmail: payload.contactEmail,
                contactPhone: payload.contactPhone,
                address: payload.address,
                city: payload.city,
                country: payload.country,
              }),
              ...confirmedKycFields({ ...payload.intakeKyc, country: payload.country }, payload.clientType),
              ...(cvFields.businessActivity !== undefined ? { businessActivity: cvFields.businessActivity } : {}),
            },
            ...(cvFields.sourceOfWealth?.length ? {
              sourceOfFundsWealth: { sourceOfWealth: arrayUnion(...cvFields.sourceOfWealth) },
            } : {}),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        await batch.commit();
        if (session) session.clientSaved = true;
      }
      if (payload.intakeFiles?.length) {
        await intakeRequest("attach", { clientId: editingClient.id, files: payload.intakeFiles });
      }

      toastRef.current?.show({
        severity: "success",
        summary: payload.documentsOnly ? "Documents saved" : "Updated",
        detail: payload.documentsOnly ? "The documents were saved for review." : `"${clientName || "Client"}" was updated.`,
        life: 2500,
      });

      await loadClients();
    } catch (err) {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: err?.message || "Update failed",
        life: 3500,
      });
      throw err;
    }
  }

  // ---------------------------
  // Hooks
  // ---------------------------

  const didLoadOnceRef = useRef(false);

  useEffect(() => {
    if (!isReady) return;

    // Avoid double-run in React StrictMode DEV
    if (didLoadOnceRef.current) return;
    didLoadOnceRef.current = true;

    loadClients();
    loadKycInboxCount();
    loadAdminOrgOptions();
    loadManagerAccessOptions();
    // eslint-disable-next-line
  }, [isReady]);

  // useEffect(() => {
  //   loadClients();
  //   loadKycInboxCount();
  //   // eslint-disable-next-line
  // }, [isAdmin, myOrgId]);

  // ✅ toast after redirect (simple)

  const toastHandledRef = useRef(false);

  useEffect(() => {
    const t = location.state?.toast;
    const toastId = location.state?.toastId;

    if (!t) return;

    // de-dupe (strictmode + manual back/forward)
    if (toastHandledRef.current) return;
    toastHandledRef.current = true;

    // optional stronger de-dupe using sessionStorage
    if (toastId) {
      const key = `toast_consumed_${toastId}`;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    }

    toastRef.current?.show(t);

    // clear state so refresh doesn't re-trigger it
    nav(location.pathname, { replace: true, state: {} });

    // eslint-disable-next-line
  }, [location.state]);

  useEffect(() => {
    if (!riskFilterOpen) return undefined;

    function handlePointerDown(event) {
      if (!riskFilterRef.current) return;
      if (riskFilterRef.current.contains(event.target)) return;
      setRiskFilterOpen(false);
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [riskFilterOpen]);

  // useEffect(() => {
  //   const t = location.state?.toast;
  //   if (!t) return;

  //   toastRef.current?.show(t);
  //   nav(location.pathname, { replace: true, state: {} });
  //   // eslint-disable-next-line
  // }, [location.state]);

  const hasKycToReview = kycInboxCount > 0;

  // console.log(kycInboxCount);

  const titleText = isAdmin ? "ADMIN · Clients" : "CLIENTS";

  // const titleText = isAdmin
  //   ? "ADMIN · Clients"
  //   : orgName
  //   ? `${orgName.toUpperCase()} · Clients`
  //   : "CLIENTS";

  const canAddClient = !isAdmin && (role !== "manager" || !!managerCreationTarget);
  const addClientDisabledTitle = isAdmin
    ? "Admin accounts cannot create clients."
    : role === "manager" && !managerCreationTarget
    ? "Select a team or staff member before adding a client."
    : "Add a new client";

  useStaffTopBand(
    {
	      title: titleText,
	      titleIcon: iconHomeA,
	      logoSrc: clientsTopLogo,
	      contentClassName: "clientsContentWrap",
      smallBandColor: "#f7e9d0",
      titleColor: "#7f848b",
      right: (
        <div className="d-flex gap-2">
          <Link
            to="/kyc-inbox"
            className="btn topBarBtn btn-outline-secondary btn-sm d-inline-flex align-items-center"
            style={{
              whiteSpace: "nowrap",
              paddingRight: 13,
              paddingLeft: 13,
              marginRight: 8,
              overflow: "visible",
            }}
          >
            <span className="position-relative d-inline-flex align-items-center">
              <span className="me-2">Inbox</span>

              <i
                className={`bi ${
                  hasKycToReview ? "bi-mailbox2-flag" : "bi-mailbox"
                }`}
                style={{ fontSize: 20, verticalAlign: "text-bottom" }}
                aria-hidden="true"
              />

              {kycInboxCount > 0 && (
                <span
                  className="position-absolute top-0 start-100 badge rounded-pill"
                  style={{
                    fontSize: 11,
                    transform: "translate(25%, -50%)",
                    padding: "0.35em 0.55em",
                    backgroundColor: "#C88294",
                    color: "#fff",
                  }}
                >
                  {kycInboxCount > 99 ? "99+" : kycInboxCount}
                </span>
              )}
            </span>
          </Link>

          <Link
            to="/regulatory"
            className="btn topBarBtn btn-outline-secondary btn-sm d-inline-flex align-items-center"
            title="Regulatory library"
            aria-label="Regulatory library"
            style={{
              whiteSpace: "nowrap",
              paddingRight: 10,
              paddingLeft: 10,
              marginRight: 8,
            }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/law-doc-3.svg`}
              alt=""
              style={{ width: 19, height: 19, display: "block" }}
              aria-hidden="true"
            />
          </Link>

          <button
            type="button"
            className="btn topBarBtn btn-outline-secondary btn-sm"
            onClick={() => {
              loadClients();
              loadKycInboxCount();
              loadManagerAccessOptions();
            }}
            style={{ minWidth: "50px" }}
          >
            <i className="bi bi-arrow-repeat" aria-label="Refresh" style={{ fontSize: 18 }} />
          </button>

          <button
            type="button"
            className="btn topBarBtn btn-outline-secondary btn-sm"
            onClick={handleLogout}
            style={{ marginLeft: 10 }}
          >
            Log out
          </button>
        </div>
      ),
    },
    [titleText, hasKycToReview, kycInboxCount]
  );

  // ---------------------------
  // Render
  // ---------------------------
  return (
    <motion.div
      variants={pageAnim}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.25 }}
      className="clientsPage staffShellContentPage"
    >
      {/* ✅ overlay only during loading */}
      <div
        className={`clientsLoadingOverlay ${
          showLoadingUI && !err ? "isVisible" : ""
        }`}
      >
        <div className="clientsLoadingCard">
          <div className="clientsLoadingSpinner" aria-hidden="true" />
          <div className="clientsLoadingTitle">Loading...</div>
          <div className="clientsLoadingSub">
            Fetching clients data from database
          </div>
        </div>
      </div>

      <div className="clientsContentWrap py-4">
        {err && <div className="alert alert-danger">{err}</div>}

        <div className="clientsToolbar mb-3">
          <SearchBar
            value={search}
            onChange={setSearch}
            onClear={() => setSearch("")}
            placeholder="Search clients…"
            className="clientsSearch"
          />

          {isAdmin && (
            <div className="clientsOrgFilterSelect">
              <Select
                value={adminOrgSelectedOption}
                options={adminOrgSelectOptions}
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={clientsSelectStyles}
                placeholder="Search organisation..."
                onChange={(opt) => setAdminOrgFilterId(opt?.value || "")}
                aria-label="Filter clients by organisation"
              />
            </div>
          )}

          {!isAdmin && role === "manager" && (
            <div className="clientsOrgFilterSelect">
              <Select
                value={managerAccessSelectedOption}
                options={managerAccessSelectOptions}
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={clientsSelectStyles}
                placeholder="Search team or staff..."
                onChange={(opt) => setManagerAccessFilterValue(opt?.value || "")}
                aria-label="Filter clients by team or staff member"
                formatOptionLabel={(opt) => (
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <span>{opt.label}</span>
                    {opt.type === "team" && (
                      <span style={{ fontSize: 11, color: "#8a96a3" }}>
                        team ({opt.staffCount})
                      </span>
                    )}
                    {opt.type === "staff" && (
                      <span style={{ fontSize: 11, color: "#8a96a3" }}>
                        {opt.isCurrentUser ? "me" : roleLabel(opt.role)}
                      </span>
                    )}
                  </div>
                )}
              />
            </div>
          )}

          <button
            className="btn btn-sm clientsAddBtn"
            disabled={!canAddClient}
            title={addClientDisabledTitle}
            onClick={() => {
              if (!canAddClient) return;
              setIsAddOpen(true);
            }}
          >
            Add client
          </button>
          <div className="clientsRiskFilterWrap" ref={riskFilterRef}>
            <FilterButton
              className={hasActiveClientFilter ? "clientsFilterBtnActive" : ""}
              title="Filter clients by risk type"
              onClick={() => setRiskFilterOpen((open) => !open)}
            />
            {hasActiveClientFilter && <span className="clientsFilterDot" aria-hidden="true" />}
            {riskFilterOpen && (
              <div className="clientsRiskFilterPopover" role="menu" aria-label="Filter clients by risk type">
                <div className="clientsRiskFilterTitle">Risk type</div>
                <div className="clientsRiskFilterOptions">
                  {riskFilterOptions.map((option) => {
                    const selected = option.value === riskFilterValue;
                    return (
                      <button
                        type="button"
                        key={option.value || "all"}
                        className={`clientsRiskFilterOption${selected ? " isSelected" : ""}`}
                        onClick={() => {
                          setRiskFilterValue(option.value);
                          setRiskFilterOpen(false);
                        }}
                      >
                        <span>{option.label}</span>
                        {selected && <i className="bi bi-check2" aria-hidden="true" />}
                      </button>
                    );
                  })}
                </div>

                <div className="clientsRiskFilterDivider" />

                <div className="clientsRiskFilterTitle">KYC Status</div>
                <div className="clientsRiskFilterOptions">
                  {KYC_STATUS_FILTER_OPTIONS.map((option) => {
                    const selected = option.value === kycStatusFilterValue;
                    return (
                      <button
                        type="button"
                        key={option.value || "all-kyc-statuses"}
                        className={`clientsRiskFilterOption${selected ? " isSelected" : ""}`}
                        onClick={() => {
                          setKycStatusFilterValue(option.value);
                          setRiskFilterOpen(false);
                        }}
                      >
                        <span>{option.label}</span>
                        {selected && <i className="bi bi-check2" aria-hidden="true" />}
                      </button>
                    );
                  })}
                </div>

                {hasActiveClientFilter && (
                  <button
                    type="button"
                    className="clientsRiskFilterClear"
                    onClick={() => {
                      setRiskFilterValue("");
                      setKycStatusFilterValue("");
                      setRiskFilterOpen(false);
                    }}
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </div>
          {/* <FilterButton onClick={() => alert("Open filters (placeholder)")} /> */}
        </div>

        <ClientsDocumentDropZone
          enabled={canAddClient && !matchingFiles && !isAddOpen && !isEditOpen && !isViewOpen}
          onFiles={setMatchingFiles}
        >
        <div className="clientsTableScrollWrap">
          <ClientsTable
            clients={filteredClients}
            loading={loading}
            isAdmin={isAdmin}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={confirmDelete}
            onAdd={() => {
              if (!canAddClient) return;
              setIsAddOpen(true);
            }}
            formatDate={formatDate}
          />
        </div>
        </ClientsDocumentDropZone>
      </div>

      {matchingFiles && <ClientDropIntakeRouter files={matchingFiles}
        onClose={() => setMatchingFiles(null)}
        onAdd={intake => { setMatchingFiles(null); setAddClientDroppedFiles(null); setAddClientInitialIntake(intake); setIsAddOpen(true); }}
        onEdit={(client, intake) => { setMatchingFiles(null); handleEdit(client, null, intake); }} />}
      <AddClientModal
        open={isAddOpen && canAddClient}
        initialFiles={addClientDroppedFiles}
        initialIntake={addClientInitialIntake}
        onClose={() => { setIsAddOpen(false); setAddClientDroppedFiles(null); setAddClientInitialIntake(null); }}
        onSubmit={handleAddClient}
        preferredPhoneCountry={orgPreferredPhoneCountry}
      />

      <EditClientModal
        open={isEditOpen}
        client={editingClient}
        initialFiles={editClientDroppedFiles}
        initialIntake={editClientInitialIntake}
        preferredPhoneCountry={editingClientOrgPreferredPhoneCountry}
        onClose={() => {
          setIsEditOpen(false);
          setEditingClient(null);
          setEditClientDroppedFiles(null);
          setEditClientInitialIntake(null);
        }}
        onSubmit={handleUpdateClient}
      />

      <ViewClientModal
        open={isViewOpen}
        client={viewingClient}
        onClose={() => {
          setIsViewOpen(false);
          setViewingClient(null);
        }}
        onEdit={(c, files) => {
          setIsViewOpen(false);
          setViewingClient(null);
          handleEdit(c, files);
        }}
      />

      {/* Floating bottom-right admin buttons */}
      {(isAdmin || role === "manager") && (
        <div
          className="clientsBottomActionBar"
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 50,
            background: "rgba(255,255,255,0.85)",
            backdropFilter: "blur(10px)",
            boxShadow: "0 -2px 16px rgba(15,23,42,0.08)",
            borderTop: "1px solid rgba(15,23,42,0.07)",
          }}
        >
          <div className="clientsContentWrap" style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, paddingTop: "10px", paddingBottom: "10px" }}>
            {/* disclaimer hidden — moved to /login
            <div
              className="clientsBottomDisclaimer"
              title="Disclaimer: MyKYC provides information and functionality intended to assist compliance processes. Users remain solely responsible for all compliance, onboarding, due diligence, risk assessment and business decisions. Screening results may be incomplete, inaccurate or outdated and should be independently reviewed and verified before being relied upon."
            >
              <span className="clientsBottomDisclaimerLabel">Disclaimer:</span>{" "}
              MyKYC provides information and functionality intended to assist compliance processes. Users remain solely responsible for all compliance, onboarding, due diligence, risk assessment and business decisions. Screening results may be incomplete, inaccurate or outdated and should be independently reviewed and verified before being relied upon.
            </div>
            */}

            {isAdmin && (
              <button
                type="button"
                className="btn btn-sm clientsAdminBtn btn-outline-secondary"
                onClick={() => nav("/admin/aml-policy")}
                style={{ whiteSpace: "nowrap" }}
              >
                AML Policy
              </button>
            )}

            {isAdmin && (
              <button type="button" className="btn btn-sm clientsAdminBtn btn-outline-secondary"
                onClick={() => nav("/admin/fatca")} style={{ whiteSpace: "nowrap" }}>
                Fatca
              </button>
            )}

            {isAdmin && (
              <button
                type="button"
                className="btn btn-sm clientsAdminBtn btn-outline-secondary"
                onClick={() => nav("/admin/sources")}
                style={{ whiteSpace: "nowrap" }}
              >
                Sources Update
              </button>
            )}

            {(isAdmin || role === "manager") && (
              <button
                type="button"
                className="btn btn-sm clientsAdminBtn btn-outline-secondary"
                onClick={() => nav("/admin/risk-settings")}
                style={{ whiteSpace: "nowrap" }}
              >
                Risk Settings
              </button>
            )}

            {isAdmin && (
              <button
                type="button"
                className="btn btn-sm clientsAdminBtn btn-outline-secondary"
                onClick={() => nav("/admin/users")}
                style={{ whiteSpace: "nowrap" }}
              >
                Admin
              </button>
            )}
          </div>
        </div>
      )}

      <Toast ref={toastRef} />

      {/* disclaimer hidden — moved to /login
      {!isAdmin && role !== "manager" && (
        <div
          className="clientsOrgNameFooter clientsOrgNameFooter--disclaimer"
          title="Disclaimer: MyKYC provides information and functionality intended to assist compliance processes. Users remain solely responsible for all compliance, onboarding, due diligence, risk assessment and business decisions. Screening results may be incomplete, inaccurate or outdated and should be independently reviewed and verified before being relied upon."
        >
          <span className="clientsBottomDisclaimerLabel">Disclaimer:</span>{" "}
          MyKYC provides information and functionality intended to assist compliance processes. Users remain solely responsible for all compliance, onboarding, due diligence, risk assessment and business decisions. Screening results may be incomplete, inaccurate or outdated and should be independently reviewed and verified before being relied upon.
        </div>
      )}
      */}

      {orgName && (
        <div className="clientsOrgNameFooter">{orgName}</div>
      )}
    </motion.div>
  );
}
